<script setup lang="ts">
import { countries, type TCountryCode } from 'countries-list'
import SearchBox from '@/components/lib/SearchBox.vue'
import GwText from '@/components/lib/base/GwText/GwText.vue'
import { t } from '@/locales/i18n'
import { computed, ref } from 'vue'
import { LONG_INPUT_TEXT_THRESHOLD } from '@/helpers/constantsNew'
import * as R from 'remeda'

export type Country = {
  code: TCountryCode
  name: string
}

type CountrySelectProps = {
  selectedCountry: Country | null
}

const props = defineProps<CountrySelectProps>()
const emit = defineEmits<{ 'update:country': [country: Country] }>()
const isOpen = ref(false)

const options = R.pipe(
  R.entries(countries),
  R.map(([code, data]) => ({
    code,
    name: data.name,
  })),
)

const getFlagSrc = countryCode =>
  `http://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg`

const inputWidth = computed(() =>
  props.selectedCountry && props.selectedCountry.name.length > LONG_INPUT_TEXT_THRESHOLD
    ? '28rem'
    : '24rem',
)
</script>
<template>
  <div
    :style="`width: ${inputWidth}; gap: 0.125rem`"
    class="d-flex flex-column"
  >
    <GwText variant="text_white_normal_500">{{ t('payment.crypto.country_of_purchase') }}:</GwText>
    <SearchBox
      showChevrons
      :menuIsInside="false"
      :items="options"
      :keys="['name', 'code']"
      :initialValue="selectedCountry?.name"
      style="--srch-input-height: 2.5rem"
      :style="{
        '--srch-input-border-color':
          isOpen || selectedCountry ? 'var(--clr-white-0)' : 'var(--clr-grey-2)',
        '--srch-input-padding': selectedCountry ? '0 1rem 0rem 3rem' : '0.5rem',
      }"
      :placeholder="t('payment.crypto.search_country')"
      :contentStyle="`max-height: 12rem; --width: ${inputWidth}`"
      @select="emit('update:country', $event)"
      @open="value => (isOpen = value)"
    >
      <template #opener_content>
        <GwText variant="text_white_normal_500">{{ selectedCountry?.name || '' }}</GwText>
      </template>
      <template #input_content>
        <img
          v-if="selectedCountry"
          style="
            position: absolute;
            left: 0.5rem;
            top: 0.5rem;
            width: 2.25rem;
            margin-right: 0.5rem;
          "
          :src="getFlagSrc(selectedCountry.code)"
        />
      </template>
      <template #result="{ item }">
        <div class="d-flex align-center">
          <img
            :src="getFlagSrc(item.code)"
            style="width: 1.5rem; margin-right: 0.5rem"
          />
          <GwText variant="text_white_medium_500">{{ item.name }}</GwText>
        </div>
      </template>
    </SearchBox>
  </div>
</template>
